import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
// Common Components
import AppBreadCrumb from '../../common/AppBreadCrumb'; // Correct path
import AppToaster from '../../AppToaster'; // Correct path
// API & Utils
import { createPurchaseInvoice, fetchPurchaseInvoice, updatePurchaseInvoice } from '../../../api/PurchaseInvoicesAPI'; // Correct path
import { getParties } from '../../../api/PartiesAPI'; // Correct path
import { getProducts } from '../../../api/ProductsAPI'; // Correct path
import { getUoms } from '../../../api/UomsAPI'; // Correct path
import { getHoas } from '../../../api/HoasAPI'; // Correct path
import { handleApiError } from '../../../api/risapi'; // Correct path
// Hooks & Helpers
import { useNavigate, useParams } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions'; // Correct path
// Date Picker
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const PurchaseInvoiceForm = ({ formType, currentInvoiceId }) => {
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState({
        party_id: null,
        invoice_number: '',
        invoice_date: new Date(),
        billing_address: '', // Will be populated from selected party
        billing_gstin: '',
        shipping_address: '',
        shipping_gstin: '',
        items: [], // Array for invoice items
        adjustments: [], // Array for invoice-level adjustments
        create_grn_now: true, // ** The new checkbox state **
        total_amount: 0, // Calculated display value
    });
    const [errors, setErrors] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);
    const [saving, setSaving] = useState(false);

    

    // Dropdown Options
    const [partyOptions, setPartyOptions] = useState([]);
    const [partyAddressOptions, setPartyAddressOptions] = useState([]); // For address selection
    const [productOptions, setProductOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]); // All UOMs
    const [hoaOptions, setHoaOptions] = useState([]); // All HOAs for adjustments
    const [gstHoaOptions, setGstHoaOptions] = useState([]);
    const [quickAdjustmentHoaId, setQuickAdjustmentHoaId] = useState(null);
    const [quickAdjustmentPercent, setQuickAdjustmentPercent] = useState('');

    // --- Fetch Dropdown Data ---
    useEffect(() => {
        let isMounted = true;
        setLoadingDropdowns(true);
        const fetchDropdowns = async () => {
            try {
                const [partyResponse, productResponse, uomResponse, hoaResponse] = await Promise.all([
                    new Promise((resolve) => getParties({ paginate: false }, resolve)),
                    new Promise((resolve) => getProducts({ paginate: false }, resolve)), // Ensure productUnits are loaded
                    new Promise((resolve) => getUoms({ paginate: false }, resolve)),
                    new Promise((resolve) => getHoas({ paginate: false }, resolve)), // Fetch all HOAs
                ]);

                if (!isMounted) return;

                // Parties (Store full party object to access addresses later)
                if (partyResponse.parties && Array.isArray(partyResponse.parties)) {
                    setPartyOptions(partyResponse.parties.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}`, partyData: p }))); // Store full party data
                } else { throw new Error('Invalid party data'); }

                // Products (Store full product object)
                 if (productResponse.products && Array.isArray(productResponse.products)) {
                     setProductOptions(productResponse.products.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}`, productData: p }))); // Store full product data
                 } else { throw new Error('Invalid product data'); }

                // UOMs
                if (uomResponse.uoms && Array.isArray(uomResponse.uoms)) {
                     setUomOptions(uomResponse.uoms.map(u => ({ value: u.id, label: `${u.name} (${u.short_name})` })));
                 } else { throw new Error('Invalid UOM data'); }

                if (hoaResponse.hoas) {
                    const hoasData = Array.isArray(hoaResponse.hoas) ? hoaResponse.hoas : (hoaResponse.hoas.data || []);
                    // Filter for item adjustments (GST Heads)
                    setGstHoaOptions(hoasData.filter(h => h.is_gst_head).map(h => ({ value: h.id, label: h.name })));
                    // Filter for invoice adjustments (Non-GST Heads - adjust filter if needed)
                    setHoaOptions(hoasData.filter(h => !h.is_gst_head).map(h => ({ value: h.id, label: h.name })));
                 } else { throw new Error('Invalid HOA data'); }

            } catch (error) { /* ... error handling ... */ }
             finally { if (isMounted) setLoadingDropdowns(false); }
        };
        fetchDropdowns();
        return () => { isMounted = false; };
    }, []);

    // --- Fetch Invoice Data in Edit Mode ---
    useEffect(() => {
         if (loadingDropdowns || formType === 'create' || !currentInvoiceId) {
             if (formType === 'create') setLoadingInvoiceData(false); return;
         }
         let isMounted = true;
         // Reset form
         setInvoice({ party_id: null, invoice_number: '', invoice_date: new Date(), items: [], adjustments: [], create_grn_now: false, /* address fields */ }); // Disable GRN checkbox in edit
         setErrors({});
         setLoadingInvoiceData(true);

         fetchPurchaseInvoice(currentInvoiceId, (response) => {
             if (!isMounted) return;
             setLoadingInvoiceData(false);
             if (response.purchase_invoice) {
                 const fetched = response.purchase_invoice;
                 let invoiceDate = new Date(); // Default
                 if (fetched.invoice_date) { try { invoiceDate = parseISO(fetched.invoice_date + 'T00:00:00'); if(isNaN(invoiceDate)) invoiceDate = new Date(); } catch(e){ invoiceDate = new Date(); } }

                 const formattedItems = (Array.isArray(fetched.items) ? fetched.items : []).map(item => ({
                     id: item.id,
                     product_id: item.product_id,
                     purchase_uom_id: item.purchase_uom_id,
                     purchase_quantity: item.purchase_quantity || '',
                     purchase_price: item.purchase_price || '',
                     line_total: item.line_total || 0,
                     // Add item adjustments if implemented
                 }));
                 const formattedAdjustments = (Array.isArray(fetched.adjustments) ? fetched.adjustments : []).map(adj => ({
                     id: adj.id, hoa_id: adj.hoa_id,
                     percentage: adj.percentage ?? '', amount: adj.amount ?? '',
                     is_subtraction: !!adj.is_subtraction, description: adj.description || ''
                 }));

                 setInvoice({
                     ...fetched, // Include other fields like id
                     invoice_date: invoiceDate,
                     items: formattedItems,
                     adjustments: formattedAdjustments,
                     create_grn_now: false, // Cannot auto-create GRN on update
                 });
                 // Pre-populate addresses for the selected party if needed (might require fetching party again)
                 handlePartyChange({ value: fetched.party_id }); // Trigger address population
             } else { /* ... error handling ... */ }
         });
         return () => { isMounted = false; };
    }, [formType, currentInvoiceId, loadingDropdowns, navigate]);

// --- NEW: Quick Item Adjustment Handler ---
    const applyQuickAdjustment = () => {
        if (!quickAdjustmentHoaId || quickAdjustmentPercent === '' || isNaN(parseFloat(quickAdjustmentPercent))) {
            setToaster({ type: 'warning', heading: 'Missing Info', message: 'Please select a GST Head and enter a valid percentage.' });
            setShowToaster(true);
            return;
        }

        const percentValue = parseFloat(quickAdjustmentPercent);
        if (percentValue < 0) {
             setToaster({ type: 'warning', heading: 'Invalid Input', message: 'Percentage cannot be negative.' });
             setShowToaster(true);
             return;
        }

        setInvoice(prev => {
            const newItems = (prev.items || []).map(item => {
                const itemBaseAmount = parseFloat(item.purchase_quantity || 0) * parseFloat(item.purchase_price || 0);
                if (isNaN(itemBaseAmount) || itemBaseAmount <= 0) {
                    return item; // Skip if no valid base amount
                }

                let currentAdjustments = Array.isArray(item.adjustments) ? [...item.adjustments] : [];
                const calculatedAmount = ((itemBaseAmount * percentValue) / 100).toFixed(2);

                // Find if this HOA already exists for this item
                const existingAdjIndex = currentAdjustments.findIndex(adj => adj.hoa_id === quickAdjustmentHoaId);

                if (existingAdjIndex > -1) {
                    // Update existing adjustment
                    currentAdjustments[existingAdjIndex] = {
                        ...currentAdjustments[existingAdjIndex],
                        percentage: percentValue.toString(), // Store as string
                        amount: calculatedAmount, // Update calculated amount
                        is_subtraction: false // Assuming GST is always additive
                    };
                } else {
                    // Add new adjustment
                    currentAdjustments.push({
                        id: null,
                        hoa_id: quickAdjustmentHoaId,
                        percentage: percentValue.toString(),
                        amount: calculatedAmount,
                        is_subtraction: false,
                        description: '' // Or copy description from HOA?
                    });
                }
                // Recalculate item total could happen here or rely on calculateTotals effect
                return { ...item, adjustments: currentAdjustments };
            });
            return { ...prev, items: newItems };
        });

        // Optionally clear the quick adjustment inputs after applying
        // setQuickAdjustmentHoaId(null);
        // setQuickAdjustmentPercent('');

        setToaster({ type: 'success', heading: 'Applied', message: 'Adjustment applied to all items.' });
        setShowToaster(true);
    };
    // --- Input Handlers (Header) ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setInvoice(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleDateChange = (date) => { setInvoice(prev => ({ ...prev, invoice_date: date })); };

    const handlePartyChange = (selectedOption) => {
         const partyId = selectedOption ? selectedOption.value : null;
         const selectedPartyData = selectedOption ? selectedOption.partyData : null;
         setInvoice(prev => ({
             ...prev,
             party_id: partyId,
             // Reset addresses when party changes
             billing_address: '', billing_gstin: '', shipping_address: '', shipping_gstin: ''
         }));
         setPartyAddressOptions([]); // Clear address options

         // Populate addresses if party selected
         if (selectedPartyData && Array.isArray(selectedPartyData.addresses)) {
             const addresses = selectedPartyData.addresses.map(addr => ({
                 value: addr.id,
                 label: `${addr.address_type}: ${addr.address_line_1}, ${addr.city}`,
                 fullAddress: `${addr.address_line_1}${addr.address_line_2 ? '\n'+addr.address_line_2 : ''}\n${addr.city}, ${addr.state} - ${addr.pincode}`,
                 gstin: addr.gstin
             }));
             setPartyAddressOptions(addresses);

             // Auto-select default addresses
             const defaultBilling = selectedPartyData.addresses.find(a => a.is_default_billing);
             const defaultShipping = selectedPartyData.addresses.find(a => a.is_default_shipping);
             const firstAddress = addresses.length > 0 ? addresses[0] : null; // Fallback

             if (defaultBilling || firstAddress) {
                  const billingToUse = defaultBilling ? addresses.find(a=>a.value === defaultBilling.id) : firstAddress;
                  if(billingToUse){
                       setInvoice(prev => ({ ...prev, billing_address: billingToUse.fullAddress, billing_gstin: billingToUse.gstin }));
                  }
             }
             if (defaultShipping || firstAddress) {
                  const shippingToUse = defaultShipping ? addresses.find(a=>a.value === defaultShipping.id) : firstAddress;
                   if(shippingToUse){
                      setInvoice(prev => ({ ...prev, shipping_address: shippingToUse.fullAddress, shipping_gstin: shippingToUse.gstin }));
                   }
             }
         }
         // Clear relevant errors
         setErrors(prev => ({ ...prev, party_id: null }));
     };

     // Optional: Handlers to manually select billing/shipping addresses from dropdowns
     // const handleBillingAddressSelect = (selectedOption) => { setInvoice(prev => ({...prev, billing_address: selectedOption?.fullAddress, billing_gstin: selectedOption?.gstin}))};
     // const handleShippingAddressSelect = (selectedOption) => { setInvoice(prev => ({...prev, shipping_address: selectedOption?.fullAddress, shipping_gstin: selectedOption?.gstin}))};

    // --- Item Handlers ---
    const addItem = () => {
         setInvoice(prev => ({ ...prev, items: [...(prev.items || []), { id: null, product_id: null, purchase_uom_id: null, purchase_quantity: '', purchase_price: '', line_total: 0 /*, adjustments: [] */ }] }));
     };
    const removeItem = (index) => { setInvoice(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) })); };
    const handleItemChange = (index, fieldName, value) => {
        setInvoice(prev => {
            const newItems = [...(prev.items || [])];
            if (!newItems[index]) return prev;

            let updatedValue = value;
             let dependentUpdates = {};

            // If product changes, reset UOM and get valid UOMs
            if (fieldName === 'product_id') {
                 updatedValue = value ? parseInt(value, 10) : null;
                 dependentUpdates.purchase_uom_id = null; // Reset UOM
                 // Fetching UOM options per row is complex - easier to validate on submit
                 // Or store valid UOMs in productOptions and retrieve here
            } else if (fieldName === 'purchase_uom_id') {
                updatedValue = value ? parseInt(value, 10) : null;
            } else if (fieldName === 'purchase_quantity' || fieldName === 'purchase_price') {
                 updatedValue = value === '' ? '' : value; // Allow empty string for input control
            }

            // Recalculate basic line total (adjustments handled separately/on save)
            const qty = (fieldName === 'purchase_quantity') ? parseFloat(updatedValue || 0) : parseFloat(newItems[index].purchase_quantity || 0);
            const price = (fieldName === 'purchase_price') ? parseFloat(updatedValue || 0) : parseFloat(newItems[index].purchase_price || 0);
            dependentUpdates.line_total = qty * price;


            newItems[index] = { ...newItems[index], [fieldName]: updatedValue, ...dependentUpdates };
            return { ...prev, items: newItems };
        });
        // Clear specific error
        setErrors(prev => { /* ... clear item error logic ... */ return prev; });
    };

     // Helper to get valid UOMs for a specific product ID (used in item row dropdown)
     const getFilteredUomOptionsForItem = (productId) => {
         if (!productId || productOptions.length === 0 || uomOptions.length === 0) return [];
         const product = productOptions.find(p => p.value === productId)?.productData; // Get full product data
         if (!product) return [];

         const validUomIds = new Set();
         if (product.base_uom_id) validUomIds.add(product.base_uom_id);
         if (Array.isArray(product.product_units)) {
             product.product_units.forEach(unit => { if (unit.uom_id) validUomIds.add(unit.uom_id); });
         }
         return uomOptions.filter(uom => validUomIds.has(uom.value));
     };

     // --- Item Adjustment Handlers ---
     const addItemAdjustment = (itemIndex) => {
         setInvoice(prev => {
             const newItems = [...(prev.items || [])]; if (!newItems[itemIndex]) return prev;
             const currentAdjustments = Array.isArray(newItems[itemIndex].adjustments) ? newItems[itemIndex].adjustments : [];
             newItems[itemIndex].adjustments = [ ...currentAdjustments, { id: null, hoa_id: null, percentage: '', amount: '', is_subtraction: false }];
             return { ...prev, items: newItems };
         });
     };
     const removeItemAdjustment = (itemIndex, adjIndex) => {
         setInvoice(prev => {
             const newItems = [...(prev.items || [])]; if (!newItems[itemIndex]) return prev;
             const currentAdjustments = Array.isArray(newItems[itemIndex].adjustments) ? newItems[itemIndex].adjustments : [];
             newItems[itemIndex].adjustments = currentAdjustments.filter((_, i) => i !== adjIndex);
             // Recalculate item total after removing adjustment
             const itemBaseAmount = parseFloat(newItems[itemIndex].purchase_quantity || 0) * parseFloat(newItems[itemIndex].purchase_price || 0);
             const itemAdjustmentTotal = newItems[itemIndex].adjustments.reduce((sum, adj) => {
                 const adjAmount = parseFloat(adj.amount || 0); return sum + ((adj.is_subtraction ? -1 : 1) * adjAmount);
             }, 0);
             newItems[itemIndex].line_total = itemBaseAmount + itemAdjustmentTotal;
             return { ...prev, items: newItems };
         });
     };
    const handleItemAdjustmentChange = (itemIndex, adjIndex, fieldName, value) => {
         setInvoice(prev => {
             const newItems = [...(prev.items || [])]; if (!newItems[itemIndex]?.adjustments?.[adjIndex]) return prev;
             const newAdjustments = [...newItems[itemIndex].adjustments];
             let updatedValue = value; let adjustmentToUpdate = { ...newAdjustments[adjIndex] };
             const itemBaseAmount = parseFloat(newItems[itemIndex].purchase_quantity || 0) * parseFloat(newItems[itemIndex].purchase_price || 0);
             if (fieldName === 'hoa_id') { updatedValue = value ? parseInt(value, 10) : null; }
             else if (fieldName === 'is_subtraction') { updatedValue = typeof value === 'boolean' ? value : (value === 'true' || value === true); }
             else { updatedValue = value === '' ? '' : value; }
             adjustmentToUpdate[fieldName] = updatedValue;
             if (fieldName === 'percentage' && updatedValue !== '') {
                 const percentageValue = parseFloat(updatedValue);
                 adjustmentToUpdate['amount'] = (!isNaN(percentageValue) && !isNaN(itemBaseAmount) && itemBaseAmount >= 0) ? ((itemBaseAmount * percentageValue) / 100).toFixed(2) : '';
                 adjustmentToUpdate['percentage'] = updatedValue;
             } else if (fieldName === 'amount' && updatedValue !== '') {
                  adjustmentToUpdate['percentage'] = ''; adjustmentToUpdate['amount'] = updatedValue;
             }
             newAdjustments[adjIndex] = adjustmentToUpdate;
             newItems[itemIndex].adjustments = newAdjustments;
             // Recalculate item's final line total
              const itemAdjustmentTotal = newAdjustments.reduce((sum, adj) => { const adjAmount = parseFloat(adj.amount || 0); return sum + ((adj.is_subtraction ? -1 : 1) * adjAmount); }, 0);
              newItems[itemIndex].line_total = itemBaseAmount + itemAdjustmentTotal;
             return { ...prev, items: newItems };
         });
          setErrors(prev => { const newErrors = {...prev}; if(newErrors.items?.[itemIndex]?.adjustments?.[adjIndex]?.[fieldName]) delete newErrors.items[itemIndex].adjustments[adjIndex][fieldName]; return newErrors; });
     };


    // --- Adjustment Handlers (Invoice Level) ---
    const addAdjustment = () => { setInvoice(prev => ({ ...prev, adjustments: [...(prev.adjustments || []), { id: null, hoa_id: null, percentage: '', amount: '', is_subtraction: false }] })); };
    const removeAdjustment = (index) => { setInvoice(prev => ({ ...prev, adjustments: (prev.adjustments || []).filter((_, i) => i !== index) })); };
    const handleAdjustmentChange = (index, fieldName, value) => {
         setInvoice(prev => {
             const newAdjustments = [...(prev.adjustments || [])];
             if (!newAdjustments[index]) return prev;
             let updatedValue = value;
             if (fieldName === 'is_subtraction') { updatedValue = typeof value === 'boolean' ? value : (value === 'true' || value === true); }
              else if (fieldName === 'hoa_id') { updatedValue = value ? parseInt(value, 10) : null; }
               else { updatedValue = value === '' ? '' : value; }

             let adjustmentToUpdate = { ...newAdjustments[index], [fieldName]: updatedValue };
             if (fieldName === 'percentage' && updatedValue !== '') adjustmentToUpdate['amount'] = '';
             else if (fieldName === 'amount' && updatedValue !== '') adjustmentToUpdate['percentage'] = '';

             newAdjustments[index] = adjustmentToUpdate;
             return { ...prev, adjustments: newAdjustments };
         });
         // Clear specific error
         setErrors(prev => { /* ... clear adjustment error logic ... */ return prev; });
     };

        // --- Helper to recalculate adjustments ---
     const calculateItemAdjustments = (adjustments, baseAmount) => { /* ... (as before) ... */ };

    // --- Invoice Adjustment Handlers (Invoice Level) ---
     const addInvoiceAdjustment = () => { /* ... (as before) ... */ };
     const removeInvoiceAdjustment = (index) => { /* ... (as before) ... */ };
     const handleInvoiceAdjustmentChange = (index, fieldName, value) => {
         // ** Uses invoice subtotal (sum of item BASE amounts) for percentage calculation **
         setInvoice(prev => {
             const newAdjustments = [...(prev.adjustments || [])]; if (!newAdjustments[index]) return prev;
             let updatedValue = value; let adjustmentToUpdate = { ...newAdjustments[index] };
             const invoiceSubtotal = (prev.items || []).reduce((sum, item) => sum + (parseFloat(item.purchase_quantity || 0) * parseFloat(item.purchase_price || 0)), 0);
             if (fieldName === 'hoa_id') { updatedValue = value ? parseInt(value, 10) : null; }
             else if (fieldName === 'is_subtraction') { updatedValue = typeof value === 'boolean' ? value : (value === 'true' || value === true); }
             else { updatedValue = value === '' ? '' : value; }
             adjustmentToUpdate[fieldName] = updatedValue;
             if (fieldName === 'percentage' && updatedValue !== '') {
                 const percentageValue = parseFloat(updatedValue);
                 adjustmentToUpdate['amount'] = (!isNaN(percentageValue) && !isNaN(invoiceSubtotal) && invoiceSubtotal > 0) ? ((invoiceSubtotal * percentageValue) / 100).toFixed(2) : '';
                 adjustmentToUpdate['percentage'] = updatedValue;
             } else if (fieldName === 'amount' && updatedValue !== '') {
                 adjustmentToUpdate['percentage'] = ''; adjustmentToUpdate['amount'] = updatedValue;
             }
             newAdjustments[index] = adjustmentToUpdate;
             return { ...prev, adjustments: newAdjustments };
         });
          setErrors(prev => { const newErrors = {...prev}; if(newErrors.adjustments?.[index]?.[fieldName]) delete newErrors.adjustments[index][fieldName]; return newErrors; });
     };


     // --- Calculate Totals for Display ---
     const calculateTotals = useCallback(() => {
         let subtotal = 0;
         (Array.isArray(invoice.items) ? invoice.items : []).forEach(item => {
             subtotal += parseFloat(item.line_total || 0);
             // Add item adjustment calculations here if implemented
         });

         let adjustmentTotal = 0;
         (Array.isArray(invoice.adjustments) ? invoice.adjustments : []).forEach(adj => {
             let adjAmount = 0;
             if (adj.percentage !== '' && adj.percentage !== null) {
                 adjAmount = (subtotal * parseFloat(adj.percentage || 0)) / 100;
             } else {
                 adjAmount = parseFloat(adj.amount || 0);
             }
             adjustmentTotal += (adj.is_subtraction ? -1 : 1) * adjAmount;
         });

         const grandTotal = subtotal + adjustmentTotal;
         // Update state only if changed to avoid infinite loops
         if (invoice.total_amount !== grandTotal) {
             setInvoice(prev => ({ ...prev, total_amount: grandTotal }));
         }
     }, [invoice.items, invoice.adjustments, invoice.total_amount]); // Added total_amount

      // Recalculate totals whenever items or adjustments change
     useEffect(() => {
         calculateTotals();
     }, [invoice.items, invoice.adjustments, calculateTotals]);


    // --- Save Handler ---
    const saveForm = (exitForm) => {
        setErrors({});
        setSaving(true);
        const apiCall = formType === 'create' ? createPurchaseInvoice : updatePurchaseInvoice;

        // Format date
        let formattedDate = null;
        if (invoice.invoice_date instanceof Date && !isNaN(invoice.invoice_date)) {
            formattedDate = format(invoice.invoice_date, 'yyyy-MM-dd');
        } else { /* ... date error handling ... */ setSaving(false); return; }

        // Clean items and adjustments
        const cleanedItems = (Array.isArray(invoice.items) ? invoice.items : [])
             .filter(item => item.product_id && item.purchase_uom_id && item.purchase_quantity && item.purchase_price) // Basic validation
             .map(item => ({
                 id: item.id, // Keep ID for update
                 product_id: item.product_id,
                 purchase_uom_id: item.purchase_uom_id,
                 purchase_quantity: item.purchase_quantity,
                 purchase_price: item.purchase_price,
                 // Send item adjustments if implemented
             }));

         const cleanedAdjustments = (Array.isArray(invoice.adjustments) ? invoice.adjustments : [])
             .filter(adj => adj.hoa_id && (adj.percentage !== '' || adj.amount !== ''))
             .map(adj => ({
                 id: adj.id, hoa_id: adj.hoa_id,
                 percentage: adj.percentage === '' ? null : adj.percentage,
                 amount: adj.amount === '' ? null : adj.amount,
                 is_subtraction: adj.is_subtraction, description: adj.description
             }));

        if (cleanedItems.length === 0) {
             setErrors({ items: 'At least one valid item is required.'});
             setSaving(false);
             return;
        }


        const dataToSend = {
             ...invoice, // Includes party_id, invoice_number, address snapshots etc.
             invoice_date: formattedDate,
             items: cleanedItems,
             adjustments: cleanedAdjustments,
             // create_grn_now is already in invoice state
        };
        // Remove total_amount as backend calculates it
        delete dataToSend.total_amount;
         // Remove 'id' for create operation
         if (formType === 'create') { delete dataToSend.id; }


        apiCall(dataToSend, (response) => {
            setSaving(false);
            if (response.purchase_invoice) {
                 const message = `Purchase Invoice ${formType === 'create' ? 'saved' : 'updated'} successfully!` + (formType === 'create' && dataToSend.create_grn_now ? ' GRN also created.' : '');
                if (exitForm) {
                    navigate('/franchisee_user/inventory/purchase-invoices', { state: { message } });
                } else if (formType === 'create') {
                     // Reset form
                     setInvoice({ party_id: null, invoice_number: '', invoice_date: new Date(), items: [], adjustments: [], create_grn_now: true, /* address fields */ });
                     setPartyAddressOptions([]);
                    setToaster({ type: 'success', heading: 'Success', message });
                    setShowToaster(true);
                } else { // Edit + Save without exit
                     // Update state with response, reformat date/items/adjustments
                     // (Complex, might be simpler to just show toaster and maybe re-fetch)
                     setToaster({ type: 'success', heading: 'Success', message });
                     setShowToaster(true);
                     // Re-fetch to ensure consistency?
                     // fetchPurchaseInvoice(currentInvoiceId, (refetchResponse) => { ... });
                }
            } else {
                 handleApiError(response, setErrors);
                 setToaster({ type: 'danger', heading: 'Error', message: `Could not ${formType} Purchase Invoice.` });
                 setShowToaster(true);
            }
        });
    };

    // --- Reset Handler ---
    const handleReset = () => { /* ... (implement based on edit/create logic) ... */ };
    const getTitle = () => formType === 'create' ? 'Create New Purchase Invoice' : 'Edit Purchase Invoice';
    const isLoading = loadingDropdowns || loadingInvoiceData;

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    {/* <AppBreadCrumb title={getTitle()}>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li><a className="f-s-14 f-w-500" href="/franchisee_user/inventory/purchase-invoices">Purchase Invoices</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                    </AppBreadCrumb> */}

                    {isLoading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!isLoading && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header"><h5>{getTitle()}</h5></div>
                                    <div className="card-body">
                                        <form className="app-form needs-validation" noValidate onSubmit={(e) => e.preventDefault()}>

                                             {/* --- Header Section --- */}
                                             <div className="row border-bottom pb-3 mb-1">
                                                 {/* Party */}
                                                 <div className="col-md-4 mb-3">
                                                      <label className="form-label">Supplier (Party) *</label>
                                                      <Select options={partyOptions} value={partyOptions.find(o => o.value === invoice.party_id)} onChange={handlePartyChange} placeholder="Select Supplier" isClearable isDisabled={loadingDropdowns} />
                                                      <span className="text-danger">{errors?.party_id}</span>
                                                 </div>
                                                 {/* Invoice No */}
                                                 <div className="col-md-4 mb-3">
                                                      <label className="form-label">Supplier Invoice No.</label>
                                                      <input type="text" className="form-control" name="invoice_number" value={invoice.invoice_number || ''} onChange={handleInputChange} />
                                                      <span className="text-danger">{errors?.invoice_number}</span>
                                                 </div>
                                                 {/* Invoice Date */}
                                                 <div className="col-md-4 mb-3">
                                                      <label className="form-label">Invoice Date *</label>
                                                      <DatePicker selected={invoice.invoice_date} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" />
                                                      <span className="text-danger">{errors?.invoice_date}</span>
                                                 </div>
                                                 {/* Billing Address */}
                                                 <div className="col-md-3 mb-3">
                                                     <label className="form-label">Billing Address</label>
                                                      {/* Optional: Dropdown to select from party addresses */}
                                                      {/* <Select options={partyAddressOptions} ... /> */}
                                                      <textarea className="form-control" rows="3" name="billing_address" value={invoice.billing_address || ''} onChange={handleInputChange} placeholder="Billing address snapshot..."></textarea>
                                                      <input type="text" className="form-control mt-1" name="billing_gstin" value={invoice.billing_gstin || ''} onChange={handleInputChange} placeholder="Billing GSTIN" maxLength="15"/>
                                                      <span className="text-danger">{errors?.billing_address || errors?.billing_gstin}</span>
                                                 </div>
                                                  {/* Shipping Address */}
                                                 <div className="col-md-3 mb-3">
                                                      <label className="form-label">Shipping Address</label>
                                                       {/* <Select options={partyAddressOptions} ... /> */}
                                                      <textarea className="form-control" rows="3" name="shipping_address" value={invoice.shipping_address || ''} onChange={handleInputChange} placeholder="Shipping address snapshot..."></textarea>
                                                      <input type="text" className="form-control mt-1" name="shipping_gstin" value={invoice.shipping_gstin || ''} onChange={handleInputChange} placeholder="Shipping GSTIN" maxLength="15"/>
                                                      <span className="text-danger">{errors?.shipping_address || errors?.shipping_gstin}</span>
                                                 </div>
<div className='col-md-6'>                                                                    {/* --- NEW: Quick Item Adjustment Section --- */}
                            <hr className="my-4"/>
                            <h6 className="mb-3">Quick Item Adjustment (Apply GST to All Items)</h6>
                             <span className="text-danger d-block mb-2">{/* Error placeholder if needed */}</span>
                            <div className="row  align-items-end border p-3 mb-3 rounded bg-light">
                                <div className="col-md-4 mb-2">
                                     <label className="form-label">GST Head *</label>
                                     <Select
                                         options={gstHoaOptions}
                                         value={gstHoaOptions.find(o => o.value === quickAdjustmentHoaId)}
                                         onChange={(sel) => setQuickAdjustmentHoaId(sel ? sel.value : null)}
                                         placeholder="Select GST Head (e.g., CGST)"
                                         isClearable
                                         isDisabled={loadingDropdowns}
                                         menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                     />
                                </div>
                                <div className="col-md-4 mb-2">
                                     <label className="form-label">Percentage *</label>
                                     <div className="input-group">
                                          <input
                                               type="number" step="0.01" min="0" max="100"
                                               className="form-control" placeholder="e.g., 9"
                                               value={quickAdjustmentPercent}
                                               onChange={(e) => setQuickAdjustmentPercent(e.target.value)}
                                               disabled={loadingDropdowns}
                                          />
                                         <span className="input-group-text">%</span>
                                     </div>
                                </div>
                                <div className="col-md-4 mb-2">
                                    <button type="button" className="btn btn-primary w-100" onClick={applyQuickAdjustment} disabled={loadingDropdowns || saving}>
                                        Apply to All Items
                                    </button>
                                </div>
                            </div>
                            </div>



                                             </div>

                          

                                             {/* --- Items Section --- */}
                                             {/* <h6 className="mb-3">Items</h6> */}
                                             <span className="text-danger d-block mb-2">{errors?.items && typeof errors.items === 'string' ? errors.items : ''}</span>
                                             <div className="table-responsive mb-3">
                                                  <table className="table table-bordered table-sm">
                                                       <thead className='table-light'>
                                                           <tr>
                                                               <th style={{width: '30%'}}>Product *</th>
                                                               <th style={{width: '15%'}}>Purchase UOM *</th>
                                                               <th style={{width: '15%'}} className='text-end'>Quantity *</th>
                                                               <th style={{width: '15%'}} className='text-end'>Price (per UOM) *</th>
                                                               <th style={{width: '15%'}} className='text-end'>Line Total</th>
                                                               <th style={{width: '5%'}}></th> {/* Action */}
                                                           </tr>
                                                       </thead>
                                                       <tbody>
                                                {Array.isArray(invoice.items) && invoice.items.map((item, itemIndex) => (
                                                    <React.Fragment key={item.id || `item-${itemIndex}`}>
                                                        {/* Item Row */}
                                                        <tr>
                                                             {/* Product Select */}
                                                             <td><Select options={productOptions} value={productOptions.find(o => o.value === item.product_id)} onChange={(sel) => handleItemChange(itemIndex, 'product_id', sel?.value)} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/> <span className="text-danger">{errors?.items?.[itemIndex]?.product_id}</span></td>
                                                             {/* UOM Select */}
                                                             <td><Select options={getFilteredUomOptionsForItem(item.product_id)} value={uomOptions.find(o => o.value === item.purchase_uom_id)} onChange={(sel) => handleItemChange(itemIndex, 'purchase_uom_id', sel?.value)} isDisabled={!item.product_id} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}/> <span className="text-danger">{errors?.items?.[itemIndex]?.purchase_uom_id}</span></td>
                                                             {/* Qty */}
                                                             <td><input type="number" step="0.01" min="0.01" className="form-control text-end" value={item.purchase_quantity || ''} onChange={(e) => handleItemChange(itemIndex, 'purchase_quantity', e.target.value)} required /> <span className="text-danger">{errors?.items?.[itemIndex]?.purchase_quantity}</span></td>
                                                             {/* Price */}
                                                             <td><input type="number" step="0.01" min="0" className="form-control text-end" value={item.purchase_price || ''} onChange={(e) => handleItemChange(itemIndex, 'purchase_price', e.target.value)} required /> <span className="text-danger">{errors?.items?.[itemIndex]?.purchase_price}</span></td>
                                                             {/* Base Total (Qty * Price) */}
                                                             <td className='text-end align-middle'>{(parseFloat(item.purchase_quantity || 0) * parseFloat(item.purchase_price || 0)).toFixed(2)}</td>
                                                             {/* Remove Button */}
                                                             <td className='text-center align-middle'><button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeItem(itemIndex)}>&times;</button></td>
                                                        </tr>
                                                        {/* Item Adjustments Row */}
                                                        <tr>
                                                            <td colSpan="6" className="p-2 bg-light">
                                                                <div className='d-flex justify-content-end align-items-center mb-1'>
                                                                    <small className='text-muted me-auto'>Item Taxes/Adjustments (GST)</small>
                                                                    <button type="button" className="btn btn-xs btn-outline-primary py-0 px-1" onClick={() => addItemAdjustment(itemIndex)}>+ Add Tax/Adj.</button>
                                                                </div>
                                                                {/* Map Item Adjustments */}
                                                                {Array.isArray(item.adjustments) && item.adjustments.map((adj, adjIndex) => (
                                                                    <div key={adj.id || `item-${itemIndex}-adj-${adjIndex}`} className="row align-items-center gx-2 mb-1">
                                                                        {/* HOA Select (Uses gstHoaOptions) */}
                                                                        <div className="col-4"><Select options={gstHoaOptions} value={gstHoaOptions.find(o => o.value === adj.hoa_id)} onChange={(sel) => handleItemAdjustmentChange(itemIndex, adjIndex, 'hoa_id', sel?.value)} placeholder="Select GST" isClearable isDisabled={loadingDropdowns} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9998 }), control: base => ({...base, minHeight: '31px', height: '31px'}), /* other styles */ }} /> <span className="text-danger">{errors?.items?.[itemIndex]?.adjustments?.[adjIndex]?.hoa_id}</span></div>
                                                                        {/* Percentage */}
                                                                        <div className="col-2"><div className="input-group input-group-sm"><input type="number" step="0.01" min="0" className="form-control" placeholder="%" value={adj.percentage || ''} onChange={(e) => handleItemAdjustmentChange(itemIndex, adjIndex, 'percentage', e.target.value)} disabled={!!adj.amount} /><span className="input-group-text">%</span></div><span className="text-danger">{errors?.items?.[itemIndex]?.adjustments?.[adjIndex]?.percentage}</span></div>
                                                                        {/* Amount */}
                                                                        <div className="col-3"><input type="number" step="0.01" min="0" className="form-control form-control-sm" placeholder="Amount" value={adj.amount || ''} onChange={(e) => handleItemAdjustmentChange(itemIndex, adjIndex, 'amount', e.target.value)} disabled={!!adj.percentage} readOnly={!!adj.percentage} /><span className="text-danger">{errors?.items?.[itemIndex]?.adjustments?.[adjIndex]?.amount}</span></div>
                                                                        {/* Subtract */}
                                                                        <div className="col-2"><div className="form-check form-switch"><input className="form-check-input" type="checkbox" role="switch" id={`item_${itemIndex}_adj_${adjIndex}_sub`} checked={adj.is_subtraction || false} onChange={(e) => handleItemAdjustmentChange(itemIndex, adjIndex, 'is_subtraction', e.target.checked)} /><label className="form-check-label" htmlFor={`item_${itemIndex}_adj_${adjIndex}_sub`}>Sub?</label></div></div>
                                                                        {/* Remove Adj */}
                                                                        <div className="col-1 text-end"><button type="button" className="btn btn-xs btn-outline-danger py-0 px-1" onClick={() => removeItemAdjustment(itemIndex, adjIndex)}>&times;</button></div>
                                                                         {/* Hidden ID */}
                                                                         <input type="hidden" name="id" value={adj.id || ''} />
                                                                    </div>
                                                                ))}
                                                                 <span className="text-danger d-block mt-1">{errors?.items?.[itemIndex]?.adjustments && typeof errors.items[itemIndex].adjustments === 'string' ? errors.items[itemIndex].adjustments : ''}</span>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))}
                                                {/* No items row */}
                                                {(!Array.isArray(invoice.items) || invoice.items.length === 0) && ( <tr><td colSpan="6" className="text-center text-muted">No items added yet.</td></tr> )}
                                            </tbody>
                                                        <tfoot>
                                                             <tr>
                                                                 <td colSpan="6">
                                                                     <button type="button" className="btn btn-sm btn-outline-success" onClick={addItem} disabled={loadingDropdowns}>+ Add Item</button>
                                                                 </td>
                                                             </tr>
                                                        </tfoot>
                                                  </table>
                                             </div>

                                             {/* --- Invoice Adjustments Section --- */}
                                             <hr className="my-4"/>
                                             <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">Invoice Adjustments (e.g., Freight, Discounts)</h6>
                                                <button type="button" className="btn btn-sm btn-outline-success" onClick={addAdjustment} disabled={loadingDropdowns}><i className="ti ti-plus me-1"></i> Add Adjustment</button>
                                             </div>
                                             <span className="text-danger d-block mb-2">{errors?.adjustments && typeof errors.adjustments === 'string' ? errors.adjustments : ''}</span>
                                             {Array.isArray(invoice.adjustments) && invoice.adjustments.map((adj, index) => (
                                                 <div key={adj.id || `adj-${index}`} className="adjustment-block border p-3 mb-3 rounded position-relative bg-light">
                                                     <button type="button" className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1" onClick={() => removeAdjustment(index)} style={{ lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Remove Adj.">&times;</button>
                                                     <div className="row align-items-center">
                                                         {/* HOA Dropdown */}
                                                         <div className="col-md-4 mb-3">
                                                             <label className="form-label">Account *</label>
                                                             <Select options={hoaOptions} value={hoaOptions.find(o => o.value === adj.hoa_id)} onChange={(sel) => handleAdjustmentChange(index, 'hoa_id', sel?.value)} placeholder="Select Account" isClearable isDisabled={loadingDropdowns} />
                                                             <span className="text-danger">{errors?.adjustments?.[index]?.hoa_id}</span>
                                                         </div>
                                                         {/* Percentage Input */}
                                                         <div className="col-md-3 mb-3">
                                                              <label className="form-label">Percentage</label>
                                                              <div className="input-group">
                                                                  <input type="number" step="0.01" min="0" className="form-control" placeholder="e.g., 5" value={adj.percentage || ''} onChange={(e) => handleAdjustmentChange(index, 'percentage', e.target.value)} disabled={!!adj.amount} />
                                                                  <span className="input-group-text">%</span>
                                                              </div>
                                                              <span className="text-danger">{errors?.adjustments?.[index]?.percentage}</span>
                                                         </div>
                                                          {/* Amount Input */}
                                                         <div className="col-md-3 mb-3">
                                                              <label className="form-label">Amount</label>
                                                              <input type="number" step="0.01" min="0" className="form-control" placeholder="e.g., 50.00" value={adj.amount || ''} onChange={(e) => handleAdjustmentChange(index, 'amount', e.target.value)} disabled={!!adj.percentage}/>
                                                              <span className="text-danger">{errors?.adjustments?.[index]?.amount}</span>
                                                         </div>
                                                          {/* Is Subtraction Checkbox */}
                                                         <div className="col-md-2 mb-3 d-flex align-items-end">
                                                             <div className="form-check form-switch">
                                                                 <input className="form-check-input" type="checkbox" role="switch" id={`inv_is_subtraction_${index}`} name="is_subtraction" checked={adj.is_subtraction || false} onChange={(e) => handleAdjustmentChange(index, 'is_subtraction', e.target.checked)} />
                                                                 <label className="form-check-label" htmlFor={`inv_is_subtraction_${index}`}>Subtract?</label>
                                                             </div>
                                                         </div>
                                                          <input type="hidden" name="id" value={adj.id || ''} />
                                                     </div>
                                                 </div>
                                             ))}

                                            {/* --- Totals & GRN Checkbox --- */}
                                            <hr className="my-4"/>
                                            <div className="row justify-content-end">
                                                  <div className="col-md-4">
                                                       {/* Display Calculated Total */}
                                                        <div className="mb-2 row">
                                                            <label className="col-sm-5 col-form-label text-end fw-bold">Subtotal:</label>
                                                            <div className="col-sm-7">
                                                            <input type="text" readOnly disabled className="form-control-plaintext text-end fw-bold" value={ (invoice.items?.reduce((sum, i) => sum + parseFloat(i.line_total || 0), 0) || 0).toFixed(2) } />
                                                            </div>
                                                        </div>
                                                         <div className="mb-2 row">
                                                            <label className="col-sm-5 col-form-label text-end fw-bold">Adjustments:</label>
                                                            <div className="col-sm-7">
                                                            <input type="text" readOnly disabled className="form-control-plaintext text-end fw-bold" value={ (invoice.total_amount - (invoice.items?.reduce((sum, i) => sum + parseFloat(i.line_total || 0), 0) || 0)).toFixed(2) } />
                                                            </div>
                                                        </div>
                                                        <div className="mb-3 row">
                                                            <label className="col-sm-5 col-form-label text-end fw-bold fs-5">Grand Total:</label>
                                                            <div className="col-sm-7">
                                                            <input type="text" readOnly disabled className="form-control-plaintext text-end fw-bold fs-5" value={ parseFloat(invoice.total_amount || 0).toFixed(2) } />
                                                            </div>
                                                        </div>

                                                       {/* GRN Checkbox - Only in Create Mode */}
                                                       {formType === 'create' && (
                                                           <div className="form-check form-switch mb-3">
                                                             <input
                                                                 className="form-check-input"
                                                                 type="checkbox"
                                                                 role="switch"
                                                                 id="create_grn_now"
                                                                 name="create_grn_now"
                                                                 checked={invoice.create_grn_now}
                                                                 onChange={handleInputChange}
                                                             />
                                                             <label className="form-check-label" htmlFor="create_grn_now">
                                                                  Items Received in Full (Auto-create GRN)
                                                              </label>
                                                             <small className="d-block text-muted">Check this if all items listed above were received physically.</small>
                                                             <span className="text-danger d-block">{errors?.create_grn_now}</span>
                                                           </div>
                                                       )}
                                                  </div>
                                            </div>


                                            {/* --- Action Buttons --- */}
                                            <div className="col-12 mt-2">
                                                <div className="text-end border-top pt-3">
                                                    {formType === 'create' ? (
                                                        <>
                                                            {/* <button className="btn btn-outline-primary me-2 btn-sm" type="button" onClick={() => saveForm(false)} disabled={saving}>Save + Next</button> */}
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving}>Save Purchase Invoice</button>
                                                            <button className="d-none d-md-inline-block btn btn-danger btn-sm" onClick={handleReset} type="button" disabled={saving}>Reset</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving}>Update Purchase Invoice</button>
                                                            <button className="btn btn-secondary me-2 btn-sm" type="button" onClick={handleReset} disabled={saving}>Reset</button>
                                                            <button className="btn btn-danger btn-sm" type="button" onClick={() => navigate('/franchisee_user/inventory/purchase-invoices')} disabled={saving}>Cancel</button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default PurchaseInvoiceForm;